// Agent leaderboard sorted by pass rate. Client-side pagination (Kyle:
// "just expand the chart ... and front-end paginate"); the chart sizes
// to the current page's row count so overflow-y never kicks in.
import React, { useEffect, useMemo, useState } from 'react'
import { isEmpty } from 'lodash'
import { useSelector } from 'react-redux'

import { EChart } from '@/components/charts/EChart'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'

import { CHART } from '@/components/charts/chartPalette'
import { WidgetActionHint } from '../components/WidgetActionHint'
import { ChartSortControl } from '../components/ChartSortControl'
import { ExportCsvButton } from '../components/ExportCsvButton'
import { useChartSort } from '../hooks/useChartSort'
import { escapeHtml, downloadCsvFile } from '../helpers/format'
import { axisTooltip, useEntityClickEvents } from './chartWidget'

const PAGE_SIZE = 10
const ROW_PX = 26

const totalCalls = (agent) =>
  (parseInt(agent.passed_call_count, 10) || 0) + (parseInt(agent.failed_call_count, 10) || 0)

const SORT_OPTIONS = [
  { key: 'passRate', label: 'Pass rate', get: (agent) => parseFloat(agent.passing_average) || 0 },
  { key: 'totalCalls', label: 'Total calls', get: (agent) => totalCalls(agent) },
  {
    key: 'failing',
    label: 'Failing calls',
    get: (agent) => parseInt(agent.failed_call_count, 10) || 0,
  },
]

// CSV export preserved from the old dashboard's Agent Performance panel
// ("make sure that button's there, with the same data").
const downloadAgentsCsv = (agents) => {
  downloadCsvFile(
    'agent-performance.csv',
    ['Agent', 'Passing %', 'Passed calls', 'Failed calls', 'Total calls'],
    agents.map((agent) => [
      agent.full_name || '',
      (parseFloat(agent.passing_average) || 0).toFixed(2),
      parseInt(agent.passed_call_count, 10) || 0,
      parseInt(agent.failed_call_count, 10) || 0,
      totalCalls(agent),
    ])
  )
}

export const AgentPerformanceWidget = () => {
  const agents = useSelector(
    (state) => state.qaCopilot.data.reportingByUser.aggregate_scores_by_user
  )
  const loading = useSelector((state) => state.qaCopilot.loading.reportingByUser)
  const [page, setPage] = useState(0)
  const sort = useChartSort(SORT_OPTIONS, { defaultKey: 'passRate', defaultDir: 'desc' })

  // Re-ranking should start at the first page, not strand the user on a
  // now-different page 3.
  useEffect(() => {
    setPage(0)
  }, [sort.sortKey, sort.sortDir])

  // One place to sort + filter, so page slicing later is trivial.
  const sortedAgents = useMemo(() => {
    return [...(agents || [])]
      .filter((agent) => agent.full_name && totalCalls(agent) > 0)
      .sort(sort.comparator)
  }, [agents, sort.comparator])

  const pageCount = Math.max(1, Math.ceil(sortedAgents.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount - 1)
  const rows = useMemo(
    () => sortedAgents.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE),
    [sortedAgents, safePage]
  )

  const option = useMemo(
    () => ({
      tooltip: axisTooltip((params) => {
        const point = params[0]
        const row = rows[point.dataIndex] || {}
        return `${escapeHtml(row.full_name)}<br/>Pass rate: ${point.value}%<br/>Calls: ${totalCalls(row)}<br/><em>Click for details</em>`
      }),
      grid: { left: 140, right: 24, top: 12, bottom: 32 },
      xAxis: { type: 'value', name: 'Pass Rate %', max: 100 },
      yAxis: {
        type: 'category',
        data: rows.map((row) => row.full_name),
        inverse: true,
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: rows.map((row) => ({
            value: Math.round(parseFloat(row.passing_average || 0)),
            agent: row,
          })),
          itemStyle: { color: CHART.BRAND },
          cursor: 'pointer',
        },
      ],
    }),
    [rows]
  )

  const events = useEntityClickEvents('agent', 'agent')

  const canPrev = safePage > 0
  const canNext = safePage < pageCount - 1
  // Grow with page contents so scroll-in-chart never triggers; small
  // floor keeps loading/empty states from collapsing to a sliver.
  const chartHeight = Math.max(120, rows.length * ROW_PX + 80)

  return (
    <Card
      title="Agent Performance"
      info="Agents ranked by score for the current filters — click one to drill into that agent’s detail."
    >
      <WidgetActionHint kind="drawer">Click an agent to open their drawer</WidgetActionHint>
      <div className="widget-toolbar">
        <ChartSortControl sort={sort} />
        <ExportCsvButton
          data-testid="agent-performance-download"
          disabled={isEmpty(sortedAgents)}
          onClick={() => downloadAgentsCsv(sortedAgents)}
        />
      </div>
      <EChart
        option={option}
        loading={loading}
        style={{ height: `${chartHeight}px` }}
        events={events}
      />
      <div className="agent-performance-pager">
        {sortedAgents.length > PAGE_SIZE ? (
          <>
            <Button
              small
              disabled={!canPrev}
              onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            >
              Prev
            </Button>
            <span>
              Page {safePage + 1} of {pageCount} ({sortedAgents.length} agents)
            </span>
            <Button
              small
              disabled={!canNext}
              onClick={() => setPage((prev) => Math.min(pageCount - 1, prev + 1))}
            >
              Next
            </Button>
          </>
        ) : (
          <span className="agent-performance-pager-count">
            {sortedAgents.length} agent{sortedAgents.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </Card>
  )
}
